import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Seo from 'src/components/Seo';
import seoConfig from '../seo.json';
import Container from 'src/components/shared/Container';
import { Img } from 'react-image';
import { useGetContestById } from 'src/hooks/contest';
import dayjs from 'dayjs';
import { PiUsersFill } from 'react-icons/pi';
import { Button, Spinner } from 'flowbite-react';
import { formatNumberWithSuffix, formatTimeDifference } from 'shared-utils';
import Breadcrumbs from 'src/components/shared/Breadcrumbs';
import { useMutation, useQueryClient } from 'react-query';
import { registerForContest, startContest } from 'src/apis/contest';
import { useToast } from 'src/context/ToastProvider';
import { HiLockClosed } from 'react-icons/hi';
import { useCountdownV2 } from 'src/hooks/utils/useCountDownV2';
import IndicatorTabs from 'src/components/shared/IndicatorTabs';
import { ContestStatus } from 'types';
import PrizeListTable from 'src/components/contest/PrizeListTable';
import { Slide, useMediaQuery } from '@mui/material';
import ContestLeaderboardUI from 'src/components/contest/ContestLeaderboardUI';
import { get } from 'lodash';
import { TbBell } from 'react-icons/tb';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';

enum IndicatorTabsType {
	LEADERBOARD = 'leaderboard',
	PRIZES = 'prizes',
}

const ViewContests = () => {
	const navigate = useNavigate();
	const isMobile = useMediaQuery('(max-width:960px)');
	const toast = useToast();
	const { contestId } = useParams();
	const queryClient = useQueryClient();
	const seoInfo = seoConfig['contests'];
	const [selectedTab, setSelectedTab] = useState<IndicatorTabsType>(IndicatorTabsType.LEADERBOARD);

	const { data, isLoading, isFetching } = useGetContestById({
		id: contestId || '',
		onError: err => {
			console.log('data:', err);
		},
	});

	const { days, hours, minutes, seconds } = useCountdownV2(data?.contest?.startDate as Date);

	const registerForContestFn = useMutation(['registerForContest'], registerForContest, {
		onSuccess(data) {
			queryClient.refetchQueries(['getContestById']);
			toast({
				title: 'Successfully registered',
				type: 'success',
			});
		},
		onError(error: Error) {
			console.log('error:', error);
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const startContestFn = useMutation(['startContest'], startContest, {
		onSuccess(data) {
			queryClient.refetchQueries(['getContestById']);
			if (data?.contestToken) {
				navigate(`/live-contest/${contestId}`);
			} else {
				toast({
					title: `Contest Url not found`,
					type: 'error',
				});
			}
		},
		onError(error: Error) {
			console.log('error:', error);
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `${errorMessage}`,
				type: 'error',
			});
		},
	});

	useEffect(() => {
		if (!isLoading && !isFetching && !data) {
			navigate('/contests');
		}
	}, [isLoading, isFetching, data]);

	if (isLoading || isFetching) {
		return (
			<div className="flex justify-center h-full min-h-screen pt-[30px]">
				{/* <Spinner /> */}
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[40%] mx-auto " />
			</div>
		);
	}

	const disableCta =
		dayjs().isAfter(data?.contest.endDate) ||
		(data?.contest?.isRegistered && dayjs().isBefore(data?.contest?.startDate));

	return (
		<div className="relative w-full">
			{isLoading || isFetching ? (
				<div className="absolute flex justify-center inset-0 rounded-[inherit] items-start w-full h-full backdrop-blur-md z-[100]">
					<div className="min-h-screen max-h-[calc(100vh-70px)] w-full h-full flex items-center justify-center">
						{/* <Spinner /> */}
						<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
					</div>
				</div>
			) : null}

			<Seo title={seoInfo.title} description={seoInfo.description} />
			<Container className="w-[92%] max-w-[1280px] pt-2  md:pb-2 pb-10 sm:w-[96%]font-inter">
				<div className="mt-4">
					<Breadcrumbs
						name="viewMeeting"
						items={[{ label: 'Contests', link: '/contests' }, { label: data?.contest?.title || '' }]}
					/>
				</div>

				<div className="flex flex-col tlg:flex-row gap-5 tlg:gap-[44px] mt-6">
					<div className="w-full tlg:max-w-[calc(100%-348px)] space-y-[36px]">
						<div>
							{/* Contest cards */}
							<div className="flex flex-row min-h-[220px] p-4 bg-gradient-to-br from-[#B8BAE4] to-[#594398] relative rounded-[8px] isolate border border-[#EBEBF0] dark:border-[#3F3F3F]">
								<Img
									src={'/assets/contestCardBg.svg'}
									className="rounded-[inherit] absolute h-full w-full inset-0 z-[-1]"
								/>

								<div className="max-w-[69%] flex flex-col">
									<h2 className="text-xl font-inter font-bold text-transparent bg-gradient-to-br bg-clip-text from-[#FDDD48] to-[#00B7C4]">
										{data?.contest?.title}
									</h2>

									<div className="flex text-[16px] text-[#F2F2F5] font-[400] items-center flex-wrap gap-1 mt-[9px]">
										<span>{dayjs(data?.contest?.startDate).format('DD MMM, hh:mm a')}</span>
										{/* <span>{dayjs(data?.contest?.endDate).format('DD MMM, hh:mm a')}</span> */}
										<span className="text-[#F2F2F5] text-[6px] font-[400]">●</span>
										<span className="flex items-center gap-1">
											{formatTimeDifference(
												new Date(data?.contest?.startDate as Date),
												new Date(data?.contest?.endDate as Date),
											)}
										</span>
									</div>

									{data?.contest?.totalRegistrations ? (
										<span className="flex items-center gap-1 text-base text-white">
											<PiUsersFill className="text-[#73DFE7] w-[18px] h-[18px]" />{' '}
											{data?.contest?.totalRegistrations?.toLocaleString()}{' '}
											{dayjs().isAfter(data?.contest?.endDate) ? 'competed' : 'registered'}
										</span>
									) : null}

									{dayjs().isAfter(data?.contest.startDate) && dayjs().isBefore(data?.contest.endDate) ? (
										<div className="mt-auto pt-[18px]">
											<div className="text-white bg-green-500 font-[700] relative rounded-[7.784px] flex gap-[7px] pb-[2px] justify-center w-full max-w-max px-2">
												<span className="animate-ping absolute inline-flex h-full w-full rounded-[7.784px] bg-green-400 opacity-75"></span>
												Live
											</div>
										</div>
									) : dayjs().isBefore(data?.contest?.endDate) ? (
										<div className="flex flex-wrap gap-2 mt-[auto] items-center">
											<span className="text-base font-[400] text-white">Starts in</span>
											<span className="bg-[#111E2C] rounded-[7.784px] flex gap-[7px] pb-[2px] justify-center w-full max-w-[116.757px]">
												{+days !== 0 ? (
													<>
														<div className="flex flex-col justify-center text-center">
															<span className="text-[17.514px] text-[#FC0] font-[500]">{days}</span>
															<span className="text-[7.784px] text-white font-[400] mt-[-5px]">
																{+days > 1 ? 'days' : 'day'}
															</span>
														</div>
														<span className="text-[17.514px] text-white font-[500]">:</span>
													</>
												) : null}
												<div className="flex flex-col justify-center text-center">
													<span className="text-[17.514px] text-[#FC0] font-[500]">{hours}</span>
													<span className="text-[7.784px] text-white font-[400] mt-[-5px]">
														{+hours > 1 ? 'hours' : 'hour'}
													</span>
												</div>
												<span className="text-[17.514px] text-white font-[500]">:</span>
												<div className="flex flex-col justify-center text-center">
													<span className="text-[17.514px] text-[#FC0] font-[500]">{minutes}</span>
													<span className="text-[7.784px] text-white font-[400] mt-[-5px]">
														{+minutes > 1 ? 'mins' : 'min'}
													</span>
												</div>
												{+days === 0 ? (
													<>
														<span className="text-[17.514px] text-white font-[500]">:</span>
														<div className="flex flex-col justify-center text-center">
															<span className="text-[17.514px] text-[#FC0] font-[500]">{seconds}</span>
															<span className="text-[7.784px] text-white font-[400] mt-[-5px]">
																{+seconds > 1 ? 'secs' : 'sec'}
															</span>
														</div>
													</>
												) : null}
											</span>
										</div>
									) : (
										<div className="flex flex-wrap gap-2 mt-[auto] items-center">
											<span className="text-base font-[400] text-white ">Ended at </span>
											<span className="bg-[#111E2C] rounded-[7.784px] flex gap-[7px] pb-[2px] justify-center w-full max-w-fit px-1">
												<div className="flex flex-col justify-center text-center">
													<span className="text-[16px] text-[#FC0] font-[500]">
														{dayjs(data?.contest?.endDate).format('DD MMM, hh:mm a')}
													</span>
												</div>
											</span>
										</div>
									)}
								</div>

								<div className="absolute bottom-[-18%] tmd:bottom-0 right-0 h-full w-full z-[-1] flex justify-center max-w-[161px] tmd:max-w-[255px] ml-auto">
									<div className="absolute text-center pt-3 mx-auto text-white">
										<p>Prizes worth</p>
										<h4 className="text-[32px] leading-[38.4px] font-[800] [text-shadow:_0px_4px_4px_rgba(0,_0,_0,_0.25)]">
											₹{formatNumberWithSuffix(data?.contest?.totalRewardMoney || 0)}
										</h4>
									</div>
									<Img
										src="https://masai-website-images.s3.ap-south-1.amazonaws.com/trophy_9e9b5f90fd.png"
										className="w-full h-full object-contain"
									/>
								</div>
							</div>

							<div
								className="mt-[24px] tlg:max-w-[680px] text-[#555770] dark:text-white"
								dangerouslySetInnerHTML={{ __html: data?.contest?.description || '' }}
							></div>

							{dayjs().isAfter(data?.contest?.endDate) && data?.contest?.leaderboard?.length === 0 ? (
								<div className="hidden md:flex mt-[20px] md:mt-[32px] border border-[#E5B800] text-base font-[400] text-yellow-700 dark:text-[#FDED72] items-center gap-[10px] bg-white dark:bg-[#1F2A37] rounded-[8px] shadow-sm p-4">
									<div className="rounded-[8px] bg-yellow-100 dark:bg-[#FFFEE5] p-[5px] text-yellow-700 dark:text-[#E5B800]">
										<TbBell className="min-w-[20px] min-h-[20px]" />
									</div>
									<div>Contest has ended. Please wait 30 minutes for evaluation and leaderboard generation.</div>
								</div>
							) : null}

							{dayjs().isBefore(data?.contest?.endDate) ? (
								<div className="mt-[40px] hidden md:block">
									<Button
										color="purplePlaced"
										className="relative"
										disabled={disableCta || startContestFn.isLoading || registerForContestFn.isLoading}
										onClick={() => {
											if (disableCta) {
												return;
											}

											if (
												data?.contest?.userAttempt?.status &&
												[ContestStatus.GRADED, ContestStatus.ENDED].includes(data?.contest?.userAttempt?.status)
											) {
												toast({
													title: 'You have already attempted',
													type: 'warning',
												});
											} else if (data?.contest?.userAttempt?.contestToken) {
												navigate(`/live-contest/${contestId}`);
											} else if (
												data?.contest?.isRegistered ||
												(dayjs().isAfter(data?.contest?.startDate) && dayjs().isBefore(data?.contest.endDate))
											) {
												startContestFn.mutate({ contestId: contestId || '' });
											} else {
												registerForContestFn.mutate({ contestId: contestId || '' });
											}
										}}
									>
										{startContestFn.isLoading || registerForContestFn.isLoading ? (
											<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
												<Spinner />
											</div>
										) : null}

										{data?.contest?.isRegistered ? (
											<>{disableCta ? <HiLockClosed className="mr-2" /> : null} Participate</>
										) : (
											'Register Now'
										)}
									</Button>
								</div>
							) : null}
						</div>
					</div>
					<div className="w-full md:max-w-[348px]">
						{dayjs().isAfter(data?.contest?.endDate) &&
						data?.contest?.leaderboard &&
						data?.contest?.leaderboard?.length > 0 ? (
							<div>
								<div className="rounded-[8px]">
									<IndicatorTabs
										options={[
											{ value: IndicatorTabsType.LEADERBOARD, label: 'Leaderboard' },
											{ value: IndicatorTabsType.PRIZES, label: 'Prizes' },
										]}
										onChange={data => {
											setSelectedTab(data.value as IndicatorTabsType);
										}}
									/>
								</div>

								{selectedTab === IndicatorTabsType.LEADERBOARD ? (
									<div className="mt-[8px]">
										<ContestLeaderboardUI
											leaderboard={data?.contest?.leaderboard}
											userAttempt={data?.contest?.userAttempt}
										/>
									</div>
								) : (
									<PrizeListTable rewards={data?.contest?.rewards || []} />
								)}
							</div>
						) : (
							<>
								<div className="mt-2 text-xl font-inter font-bold text-[#1F3343] dark:text-[#CDF] flex">Prizes</div>

								<PrizeListTable rewards={data?.contest?.rewards || []} />
							</>
						)}
					</div>
				</div>
			</Container>

			{dayjs().isAfter(data?.contest?.endDate) && data?.contest?.leaderboard?.length === 0 ? (
				<Slide direction="up" in={isMobile} mountOnEnter unmountOnExit>
					<div className="fixed bottom-0 flex z-[10] mt-[20px] md:mt-[32px] text-sm font-[400] text-yellow-700 dark:text-[#FDED72] items-center gap-[10px] bg-white dark:bg-[#1F2A37] rounded-[8px] shadow-sm p-2">
						<div className="rounded-[8px] bg-yellow-100 dark:bg-[#FFFEE5] p-[5px] text-yellow-700 dark:text-[#E5B800]">
							<TbBell className="min-w-[20px] min-h-[20px]" />
						</div>
						<div>Contest has ended. Please wait 30 minutes for evaluation and leaderboard generation.</div>
					</div>
				</Slide>
			) : null}

			{dayjs().isBefore(data?.contest?.endDate) ? (
				<Slide direction="up" in={isMobile} mountOnEnter unmountOnExit>
					<div className="fixed bottom-0 bg-red flex items-center z-[10] w-full bg-[#F7F9FB] dark:bg-[#18222D] px-4 py-2 justify-between">
						{dayjs().isAfter(data?.contest?.startDate) ? null : (
							<div className="flex gap-2 items-center w-full max-w-[50%]">
								<span className="text-base font-[400] whitespace-pre">Starts in</span>
								<span className="bg-[#111E2C] rounded-[7.784px] flex gap-[7px] pb-[2px] justify-center w-full max-w-[116.757px]">
									{+days !== 0 ? (
										<>
											<div className="flex flex-col justify-center text-center">
												<span className="text-[17.514px] text-[#FC0] font-[500]">{days}</span>
												<span className="text-[7.784px] text-white font-[400] mt-[-5px]">
													{+days > 1 ? 'days' : 'day'}
												</span>
											</div>
											<span className="text-[17.514px] text-white font-[500]">:</span>
										</>
									) : null}
									<div className="flex flex-col justify-center text-center">
										<span className="text-[17.514px] text-[#FC0] font-[500]">{hours}</span>
										<span className="text-[7.784px] text-white font-[400] mt-[-5px]">
											{+hours > 1 ? 'hours' : 'hour'}
										</span>
									</div>
									<span className="text-[17.514px] text-white font-[500]">:</span>
									<div className="flex flex-col justify-center text-center">
										<span className="text-[17.514px] text-[#FC0] font-[500]">{minutes}</span>
										<span className="text-[7.784px] text-white font-[400] mt-[-5px]">
											{+minutes > 1 ? 'mins' : 'min'}
										</span>
									</div>
									{+days === 0 ? (
										<>
											<span className="text-[17.514px] text-white font-[500]">:</span>
											<div className="flex flex-col justify-center text-center">
												<span className="text-[17.514px] text-[#FC0] font-[500]">{seconds}</span>
												<span className="text-[7.784px] text-white font-[400] mt-[-5px]">
													{+seconds > 1 ? 'secs' : 'sec'}
												</span>
											</div>
										</>
									) : null}
								</span>
							</div>
						)}

						<div className="w-full">
							<Button
								size="sm"
								color="purplePlaced"
								className="relative w-fit ml-auto"
								disabled={disableCta || startContestFn.isLoading || registerForContestFn.isLoading}
								onClick={() => {
									if (disableCta) {
										return;
									}

									if (
										data?.contest?.userAttempt?.status &&
										[ContestStatus.GRADED, ContestStatus.ENDED].includes(data?.contest?.userAttempt?.status)
									) {
										toast({
											title: 'You have already attempted',
											type: 'warning',
										});
									} else if (data?.contest?.userAttempt?.contestToken) {
										navigate(`/live-contest/${contestId}`);
									} else if (
										data?.contest?.isRegistered ||
										(dayjs().isAfter(data?.contest?.startDate) && dayjs().isBefore(data?.contest.endDate))
									) {
										startContestFn.mutate({ contestId: contestId || '' });
									} else {
										registerForContestFn.mutate({ contestId: contestId || '' });
									}
								}}
							>
								{startContestFn.isLoading || registerForContestFn.isLoading ? (
									<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
										<Spinner />
									</div>
								) : null}

								{data?.contest?.isRegistered ? (
									<>{disableCta ? <HiLockClosed className="mr-2" /> : null} Participate</>
								) : (
									'Register Now'
								)}
							</Button>
						</div>
					</div>
				</Slide>
			) : null}
		</div>
	);
};

export default ViewContests;
